import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Share2, 
  Download, 
  Plus, 
  ArrowLeft,
  CheckSquare 
} from 'lucide-react';
import { RetrospectiveBoard, StickyNote, ActionItem, User } from '../types';
import { useSocket } from '../context/SocketContext';
import { useApiClient } from '../utils/authenticatedApiClient';
import { AuthButton } from '../components/AuthButton';
import StickyNoteComponent from '../components/StickyNoteComponent';
import AddNoteModal from '../components/AddNoteModal';
import ActionItemsPanel from '../components/ActionItemsPanel';
import UsersList from '../components/UsersList';
import ExportModal from '../components/ExportModal';
import toast from 'react-hot-toast';

const BoardPage: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const { socket, isConnected } = useSocket();
  const apiClient = useApiClient();
  
  const [board, setBoard] = useState<RetrospectiveBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddNoteModal, setShowAddNoteModal] = useState<{
    show: boolean;
    columnId?: string;
  }>({ show: false });
  const [showActionItems, setShowActionItems] = useState(false);
  const [showUsersList, setShowUsersList] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [connectedUsers] = useState<User[]>([]);
  const connectedUsersRef = useRef<User[]>([]);
  const [hasJoinedBoard, setHasJoinedBoard] = useState(false);
  const boardIdRef = useRef<string | null>(null);
  // Tracks note IDs already applied to state — prevents duplicates when the
  // server emits note-added to both the room and the sender socket.
  const seenNoteIds = useRef<Set<string>>(new Set());
  
  // Anonymous user setup
  const [currentUser] = useState<User>(() => ({
    name: `Anonymous ${Math.floor(Math.random() * 1000)}`,
    color: `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`,
    isAnonymous: true
  }));

  const fetchBoard = useCallback(async () => {
    if (!boardId) return;
    
    try {
      const response = await apiClient.get<RetrospectiveBoard>(`/api/boards/${boardId}`);
      if (response.data) {
        setBoard(response.data);
      } else if (response.error?.includes('404') || response.error?.includes('not found')) {
        toast.error('Board not found');
        navigate('/');
      } else {
        throw new Error(response.error || 'Failed to fetch board');
      }
    } catch (error) {
      console.error('Error fetching board:', error);
      toast.error('Failed to load board');
    } finally {
      setLoading(false);
    }
  }, [boardId, navigate, apiClient]);

  useEffect(() => {
    if (!boardId) {
      navigate('/');
      return;
    }
    
    fetchBoard();
  }, [boardId, fetchBoard, navigate]);

  // Keep boardIdRef in sync with current board id
  useEffect(() => {
    boardIdRef.current = board?.id ?? null;
  }, [board?.id]);

  // Join board only once when socket and board are ready
  useEffect(() => {
    if (!socket || !board || hasJoinedBoard) return;

    socket.emit('join-board', { boardId: board.id, user: currentUser });
    setHasJoinedBoard(true);
  }, [socket, board, hasJoinedBoard, currentUser]);

  // Leave board on unmount or socket disconnect — isolated so board state
  // changes don't accidentally trigger leave-board
  useEffect(() => {
    if (!socket || !hasJoinedBoard) return;
    return () => {
      if (boardIdRef.current) {
        socket.emit('leave-board', { boardId: boardIdRef.current });
      }
      setHasJoinedBoard(false);
    };
  }, [socket, hasJoinedBoard]);

  // Setup socket listeners — depends only on socket so listener
  // registration is stable and doesn't tear down on every board state change
  useEffect(() => {
    if (!socket) return;

    socket.off('board-updated');
    socket.off('note-added');
    socket.off('note-updated');
    socket.off('note-deleted');
    socket.off('action-added');
    socket.off('user-joined');
    socket.off('user-left');
    socket.off('users-list');

    // Socket event listeners
    socket.on('board-updated', (data: { board: RetrospectiveBoard }) => {
      setBoard(data.board);
    });

    socket.on('note-added', (data: { boardId: string; note: StickyNote }) => {
      if (data.boardId !== boardIdRef.current) return;
      if (seenNoteIds.current.has(data.note.id)) return;
      seenNoteIds.current.add(data.note.id);
      setBoard(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === data.note.column
              ? { ...col, notes: [...col.notes, data.note] }
              : col
          ),
        };
      });
    });

    socket.on('note-updated', (data: { boardId: string; note: StickyNote }) => {
      if (data.boardId === boardIdRef.current) {
        setBoard(prev => {
          if (!prev) return prev;
          const newBoard = { ...prev };
          const column = newBoard.columns.find(col => col.id === data.note.column);
          if (column) {
            const noteIndex = column.notes.findIndex(note => note.id === data.note.id);
            if (noteIndex !== -1) {
              column.notes[noteIndex] = data.note;
            }
          }
          return newBoard;
        });
      }
    });

    socket.on('note-deleted', (data: { boardId: string; noteId: string }) => {
      if (data.boardId === boardIdRef.current) {
        setBoard(prev => {
          if (!prev) return prev;
          const newBoard = { ...prev };
          newBoard.columns.forEach(column => {
            column.notes = column.notes.filter(note => note.id !== data.noteId);
          });
          return newBoard;
        });
      }
    });

    socket.on('action-added', (data: { boardId: string; action: ActionItem }) => {
      if (data.boardId === boardIdRef.current) {
        setBoard(prev => {
          if (!prev) return prev;
          if (prev.actionItems.some(a => a.id === data.action.id)) return prev;
          return {
            ...prev,
            actionItems: [...prev.actionItems, data.action]
          };
        });
      }
    });

    // Handle user joined directly in this useEffect to avoid conflicts
    socket.on('user-joined', (data: { user: User }) => {
      // Only show notification if it's not the current user
      if (data.user.id !== socket.id) {
        // Check if user already exists using ref (synchronous)
        const userExists = connectedUsersRef.current.some(user => user.id === data.user.id);
        
        if (!userExists) {
          // Toast disabled for debugging
          // toast(`${data.user.name} joined the board`, {
          //   icon: '👋',
          //   duration: 2000,
          // });
          
          // User list updates disabled for debugging
          // setConnectedUsers(prev => {
          //   const newUsers = [...prev, data.user];
          //   connectedUsersRef.current = newUsers; // Keep ref in sync
          //   return newUsers;
          // });
        }
      }
    });

    socket.on('user-left', (data: { userId?: string }) => {
      // User list updates disabled for debugging
      // setConnectedUsers(prev => {
      //   const newUsers = prev.filter(user => user.id !== data.userId);
      //   connectedUsersRef.current = newUsers; // Keep ref in sync
      //   return newUsers;
      // });
    });

    socket.on('users-list', (data: { users: User[] }) => {
      // User list updates disabled for debugging
      // setConnectedUsers(data.users);
      // connectedUsersRef.current = data.users; // Keep ref in sync
    });

    return () => {
      socket.off('board-updated');
      socket.off('note-added');
      socket.off('note-updated');
      socket.off('note-deleted');
      socket.off('action-added');
      socket.off('user-joined');
      socket.off('user-left');
      socket.off('users-list');
    };
  }, [socket]);

  const handleDeleteNote = async (noteId: string) => {
    if (!board) return;

    if (socket && socket.connected) {
      socket.emit('note-deleted', { boardId: board.id, noteId });
    } else {
      const response = await apiClient.delete(`/api/boards/${board.id}/notes/${noteId}`);
      if (response.error) {
        toast.error('Failed to delete note: ' + response.error);
        return;
      }
    }
    // Optimistic local update
    setBoard(prev => {
      if (!prev) return prev;
      const newBoard = { ...prev };
      newBoard.columns = newBoard.columns.map(col => ({
        ...col,
        notes: col.notes.filter(note => note.id !== noteId),
      }));
      return newBoard;
    });
  };

  const handleUpdateNote = async (updatedNote: StickyNote) => {
    if (!board) return;

    if (socket && socket.connected) {
      socket.emit('note-updated', { boardId: board.id, note: updatedNote });
    } else {
      const { id, timestamp, author, ...fields } = updatedNote;
      const response = await apiClient.put<StickyNote>(
        `/api/boards/${board.id}/notes/${updatedNote.id}`,
        fields
      );
      if (response.error) {
        toast.error('Failed to update note: ' + response.error);
        return;
      }
    }
    // Optimistic local update
    setBoard(prev => {
      if (!prev) return prev;
      const newBoard = { ...prev };
      newBoard.columns = newBoard.columns.map(col => ({
        ...col,
        notes: col.notes.map(n => (n.id === updatedNote.id ? updatedNote : n)),
      }));
      return newBoard;
    });
  };
  const getColumnTypeClass = (columnTitle: string): string => {
    const title = columnTitle.toLowerCase();
    
    // Positive sections
    if (title.includes('glad') || title.includes('keep') || title.includes('start') || 
        title.includes('what went well') || title.includes('good') || title.includes('positive')) {
      return 'positive';
    }
    
    // Negative sections  
    if (title.includes('mad') || title.includes('stop') || title.includes('problem') || 
        title.includes('bad') || title.includes('negative')) {
      return 'negative';
    }
    
    // Neutral/improvement sections
    if (title.includes('sad') || title.includes('continue') || title.includes('try') || 
        title.includes('improvement') || title.includes('change') || title.includes('better')) {
      return 'neutral';
    }
    
    // Default to neutral if no match
    return 'neutral';
  };

  const handleNoteSubmit = async (noteData: Omit<StickyNote, 'id' | 'timestamp'>) => {
    if (!board) return;

    const noteWithAuthor = {
      ...noteData,
      author: currentUser.name,
    };

    if (socket && socket.connected) {
      socket.emit('note-added', { 
        boardId: board.id, 
        note: noteWithAuthor 
      });
    } else {
      // Fallback: add note via REST API when socket is unavailable
      const response = await apiClient.post<StickyNote>(
        `/api/boards/${board.id}/notes`,
        noteWithAuthor
      );
      if (response.data) {
        // Mark the ID as seen so the socket broadcast from the server is ignored
        seenNoteIds.current.add(response.data.id);
        setBoard(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            columns: prev.columns.map(col =>
              col.id === response.data!.column
                ? { ...col, notes: [...col.notes, response.data!] }
                : col
            ),
          };
        });
        toast.success('Note added');
      } else {
        toast.error('Failed to add note: ' + (response.error || 'Unknown error'));
      }
    }
  };

  const handleShareBoard = () => {
    if (board) {
      navigator.clipboard.writeText(board.shareableLink);
      toast.success('Board link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading board...</p>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Board not found</h2>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="board-container">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{board.title}</h1>
                {board.teamName && (
                  <p className="text-sm text-gray-600">{board.teamName}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <AuthButton />
              
              {!isConnected && (
                <div className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                  Connecting...
                </div>
              )}
              
              <button
                onClick={() => setShowUsersList(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Users className="h-4 w-4" />
                <span>{connectedUsers.length}</span>
              </button>
              
              <button
                onClick={() => setShowActionItems(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
              >
                <CheckSquare className="h-4 w-4" />
                <span>Actions ({board.actionItems.length})</span>
              </button>
              
              <button
                onClick={handleShareBoard}
                className="flex items-center space-x-1 px-3 py-2 bg-purple-100 text-purple-800 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>
              
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-indigo-100 text-indigo-800 rounded-lg hover:bg-indigo-200 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Board Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {board.columns.map((column) => (
            <div
              key={column.id}
              className={`column-container ${getColumnTypeClass(column.title)} p-6 min-h-[600px]`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {column.title}
                </h2>
                <button
                  onClick={() => setShowAddNoteModal({ show: true, columnId: column.id })}
                  className="p-2 bg-white/50 hover:bg-white/70 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                {column.notes.map((note) => (
                  <StickyNoteComponent
                    key={note.id}
                    note={note}
                    onUpdate={handleUpdateNote}
                    onDelete={handleDeleteNote}
                  />
                ))}
                
                {column.notes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No notes yet</p>
                    <button
                      onClick={() => setShowAddNoteModal({ show: true, columnId: column.id })}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                    >
                      Add the first note
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {showAddNoteModal.show && (
        <AddNoteModal
          columnId={showAddNoteModal.columnId!}
          columns={board.columns}
          onAdd={handleNoteSubmit}
          onClose={() => setShowAddNoteModal({ show: false })}
        />
      )}

      {showActionItems && (
        <ActionItemsPanel
          boardId={board.id}
          actionItems={board.actionItems}
          notes={board.columns.flatMap(col => col.notes)}
          onClose={() => setShowActionItems(false)}
        />
      )}

      {showUsersList && (
        <UsersList
          users={connectedUsers}
          onClose={() => setShowUsersList(false)}
        />
      )}

      {showExportModal && (
        <ExportModal
          board={board}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default BoardPage;